/* eslint-disable prettier/prettier */
import React, { FC } from 'react';
import classNames from 'classnames';
import { useExternalScript, WindowWidthContext } from '@minecraft.utils';
import { BasicContainer, FlexCol, Loader } from '@minecraft.atoms';
import { useFeatureExperiment, FEATURE_FLAGS } from '@blocs.features-experiments';
import { QUERY_PARAMS } from '@blocs.auth';
import Modal from '@minecraft.modal';
import { getEnvironment } from '@minecraft.environment';
import { RecurlyProvider } from '@recurly/react-recurly';
import { useGlobalNotification } from '@minecraft.notification-provider';
import { PaywallLocation } from './enums/PaywallLocation';
import { useBillingPopup } from './BillingPopupContext';
import { usePaywall } from './usePaywall';
import { PAYWALL_STEPS } from './enums/paywall';
import { PlanSelectionWrapper } from './PlanSelection/PlanSelectionWrapper';
import { CheckoutWrapper } from './Checkout/CheckoutWrapper';
import { OrderSuccessWraper } from './OrderSuccess/OrderSuccessWrapper';
import { usePaywallRenewal } from './usePaywallRenewal';
import { BillingCodes, MediaPurchase, TemperatureValues } from './enums/plans';
import { usePurchaseMedia } from './usePurchaseMedia';
import { Purchase } from './types';
import { SinglePageUpgradeModalExperimentGroups } from './enums/experimentGroups';
import { ThreeDActionType, ThreeDSecureForm } from './ThreeDSecureFlow';
import { useTranslation } from '../I18N';
import { captureExceptionEvent } from '../Sentry';

export interface PaywallModalProps {
  temperature?: TemperatureValues | '';
}

export interface PaywallWrapperProps {
  isModal?: boolean;
  startingStep?: PAYWALL_STEPS;
  paywallLocation?: PaywallLocation;
  onPaymentCompleteRedirect?: (_?: string) => void;
  mediaIds?: number[] | null;
}

interface PaywallWrapperWithModalProps {
  isModal: boolean;
  title: string | React.ReactNode;
  visible: boolean;
  children?: React.ReactNode;
  step?: PAYWALL_STEPS;
  onModalClose?: () => void;
}

const PaywallWrapperWithModal: FC<PaywallWrapperWithModalProps> = ({
  isModal,
  title,
  visible,
  children,
  step,
  onModalClose,
}) => {
  const { location } = useBillingPopup();
  const isSuccess = step === PAYWALL_STEPS.SUCCESS;
  const isMobile = WindowWidthContext.useIsMobile();
  const isSinglePageUpgradeModal =
    useFeatureExperiment(FEATURE_FLAGS.SINGLE_PAGE_UPGRADE_MODAL) === SinglePageUpgradeModalExperimentGroups.Variant;
  const isSuccessOrSinglePageUpgradeModal = isSuccess || isSinglePageUpgradeModal;

  if (isModal) {
    return (
      <Modal
        title={title}
        isHeaderHidden={isSuccessOrSinglePageUpgradeModal || !isMobile}
        modalSize={isSinglePageUpgradeModal ? 'large' : 'xlarge'}
        isFooterHidden
        handleToggleClick={onModalClose}
        isOpen={visible}
        data-testid="next-billing-popup-modal-wrapper"
        bodyProps={{ className: isSuccessOrSinglePageUpgradeModal || isMobile ? 'cn_atom_p-0' : 'cn_atom_pt-8' }}
        customHeaderPadding={{ top: 1, bottom: 1, left: 0, right: 0 }}
        closeIconColor={isSinglePageUpgradeModal ? 'inverted' : undefined}
        noPadding={isSuccessOrSinglePageUpgradeModal}
      >
        {children}
      </Modal>
    );
  }

  return (
    <BasicContainer
      w="full"
      m="auto"
      className={`${location !== PaywallLocation.MEMBERSHIP && !isSuccess ? 'cn_max-w-lg' : ''}`}
      data-testid="next-paywall-wrapper"
    >
      {children}
    </BasicContainer>
  );
};

export const PaywallWrapper: React.FC<PaywallWrapperProps> = React.memo(
  ({
    isModal = false,
    startingStep = PAYWALL_STEPS.INITIAL,
    onPaymentCompleteRedirect = () => null,
    mediaIds: mediaIdFromProps,
  }) => {
    const { t } = useTranslation();
    const { showMessage } = useGlobalNotification();

    const config = getEnvironment();
    const billingPlatformPublicKey = config['RECURLY_PUBLIC_KEY'];
    const [externalScriptLoaded, loadBillingScriptError] = useExternalScript('https://js.recurly.com/v4/recurly.js');

    const {
      visible,
      location,
      mediaIds: mediaIdsFromContext,
      close: closeBillingPopup,
      subscription,
      countryCode,
      plans,
      isTrialEligible,
      selectedPaymentOption,
      setCompletedPurchase,
      setCompletePurchaseError,
      isAnnualRenewalDue,
      completedPurchase,
      completePurchaseLoading,
      currencyCode: subscriptionCurrencyCode,
      currentPurchase,
      addPaymentError,
      addPaymentLoading,
      applyCouponError,
      completePurchaseError,
      couponLoading,
      onAddPaymentOption,
      applyCoupon,
      removeCoupon,
      setSelectedPaymentOption,
      onThreeDActionError,
      plansError,
      plansLoading,
      setShowAddPaymentMethod,
      showAddPaymentMethod,
      threeDActionContext,
      wallet,
      walletLoading,
      isWithinTrialPeriod,
      completePurchase,
      isReactivate,
      selectedPlan,
      setSelectedPlan,
      currentPlan,
    } = useBillingPopup();

    const mediaIds = mediaIdFromProps || mediaIdsFromContext;
    const isDeeplink = localStorage.getItem(QUERY_PARAMS.deeplink as string)?.toLocaleLowerCase() === 'true';
    const isMobile = WindowWidthContext.useIsMobile();
    const startPaywallStep = isDeeplink ? PAYWALL_STEPS.CHECKOUT : startingStep;
    const hideIsAnnualToggle = !plans?.length && !isModal;
    const isAnnualToggleLocked = plans?.length === 1;

    const {
      previewMediaPurchase,
      previewMediaPurchaseLoading,
      previewMediaPurchaseError,
      completePurchaseMedia,
      applyPurchaseMediaCoupon,
      removePurchaseMediaCoupon,
      applyPurchaseMediaCouponError,
      purchaseMediaCouponLoading,
      currencyCode: mediaPurchaseCurrencyCode,
    } = usePurchaseMedia({
      selectedPlan,
      selectedPaymentOption,
      paywallLocation: location,
      setCompletedPurchase,
      setCompletePurchaseError,
      mediaIds,
    });

    const { step, getTitle, onNext, onBack, resetStep, groupedPlans, setIsAnnual, isAnnual } = usePaywall({
      startingStep: startPaywallStep,
      selectedPlan,
      setSelectedPlan,
      closeBillingPopup,
      paywallLocation: isModal && !visible ? undefined : location,
      plans,
      mediaIds,
      isModal,
      currentPlan,
    });

    const { currentRenewalPurchase, completeRenewalPurchase, renewalCurrencyCode, enableAnnualRenewal } =
      usePaywallRenewal({
        selectedPlan,
        selectedPaymentOption,
        paywallLocation: location,
        setCompletedPurchase,
        setCompletePurchaseError,
        isAnnualRenewalDue,
        isAnnual,
      });

    const isMediaPurchase = selectedPlan?.billingCode === MediaPurchase.alacarte;
    const purchasePreview = (isMediaPurchase ? previewMediaPurchase : currentPurchase) as Purchase;
    const completePurchaseMutation = isMediaPurchase ? completePurchaseMedia : completePurchase;
    const onApplyCoupon = isMediaPurchase ? applyPurchaseMediaCoupon : applyCoupon;
    const applyCouponErr = isMediaPurchase ? applyPurchaseMediaCouponError : applyCouponError;
    const onRemoveCoupon = isMediaPurchase ? removePurchaseMediaCoupon : removeCoupon;
    const isCouponLoading = isMediaPurchase ? purchaseMediaCouponLoading : couponLoading;
    const currencyCode = isMediaPurchase ? mediaPurchaseCurrencyCode : subscriptionCurrencyCode;

    const onModalClose = () => {
      resetStep();
      closeBillingPopup();
    };

    const onCompleteRedirect = (url?: string) => {
      onPaymentCompleteRedirect(url);
      resetStep();
    };

    return (
      <PaywallWrapperWithModal
        isModal={isModal}
        title={getTitle({ paywallLocation: location, isHidden: !(isMobile && isModal), isReactivate })}
        visible={visible}
        step={step}
        onModalClose={onModalClose}
      >
        <Loader
          loading={!externalScriptLoaded && !loadBillingScriptError}
          error={loadBillingScriptError && t('common:globalErrors.generic')}
        >
          <RecurlyProvider publicKey={billingPlatformPublicKey} required={['cvv', 'postal_code']}>
            <FlexCol
              w="full"
              className={classNames({ 'cn_mt-2': !isModal })}
              justifyContent="center"
              alignItems="center"
            >
              {PAYWALL_STEPS.SUCCESS === step && (
                <OrderSuccessWraper
                  isModal={isModal}
                  selectedPlan={selectedPlan}
                  completedPurchase={completedPurchase}
                  lessMd={isMobile}
                  completePurchaseLoading={completePurchaseLoading}
                  paywallLocation={location}
                  onPaymentCompleteRedirect={onCompleteRedirect}
                  subscription={subscription}
                  countryCode={countryCode}
                  currencyCode={currencyCode}
                  closeBillingPopup={onModalClose}
                />
              )}
              {PAYWALL_STEPS.CHECKOUT === step && (
                <CheckoutWrapper
                  addPaymentError={addPaymentError}
                  addPaymentLoading={addPaymentLoading}
                  applyCouponError={applyCouponErr}
                  completePurchaseError={completePurchaseError}
                  completedPurchase={completedPurchase}
                  completePurchaseLoading={completePurchaseLoading}
                  countryCode={countryCode}
                  couponLoading={isCouponLoading}
                  currencyCode={enableAnnualRenewal ? renewalCurrencyCode : currencyCode}
                  currentPlanCode={subscription?.plan?.billingCode as BillingCodes}
                  currentPurchase={enableAnnualRenewal ? currentRenewalPurchase : purchasePreview}
                  enableAnnualRenewal={enableAnnualRenewal}
                  getTitle={getTitle}
                  isAnnual={isAnnual}
                  isModal={isModal}
                  lessMd={isMobile}
                  onAddPaymentOption={onAddPaymentOption}
                  onApplyCoupon={onApplyCoupon}
                  onBack={onBack}
                  onCompletePurchase={enableAnnualRenewal ? completeRenewalPurchase : completePurchaseMutation}
                  onNext={onNext}
                  onRemoveCoupon={onRemoveCoupon}
                  onSelectPaymentOption={setSelectedPaymentOption}
                  onThreeDActionError={onThreeDActionError}
                  paywallLocation={location}
                  plans={groupedPlans}
                  plansError={plansError}
                  plansLoading={plansLoading}
                  selectedPaymentOption={selectedPaymentOption}
                  selectedPlan={selectedPlan}
                  selectedPlanTerm={isAnnual ? 'annual' : 'monthly'}
                  setIsAnnual={setIsAnnual}
                  setSelectedPlan={setSelectedPlan}
                  setShowAddPaymentMethod={setShowAddPaymentMethod}
                  showAddPaymentMethod={showAddPaymentMethod}
                  startingStep={startPaywallStep}
                  threeDActionContext={threeDActionContext}
                  wallet={wallet}
                  walletLoading={walletLoading}
                  isWithinTrialPeriod={isWithinTrialPeriod}
                  previewMediaPurchase={previewMediaPurchase}
                  previewMediaPurchaseLoading={previewMediaPurchaseLoading}
                  previewMediaPurchaseError={previewMediaPurchaseError}
                  closeBillingPopup={onModalClose}
                  onPaymentCompleteRedirect={onCompleteRedirect}
                  isAnnualToggleLocked={isAnnualToggleLocked}
                />
              )}
              {![PAYWALL_STEPS.CHECKOUT, PAYWALL_STEPS.SUCCESS].includes(step) && (
                <PlanSelectionWrapper
                  onNext={onNext}
                  isAnnual={isAnnual}
                  plans={groupedPlans}
                  plansError={plansError}
                  plansLoading={plansLoading}
                  setIsAnnual={setIsAnnual}
                  selectedPlan={selectedPlan}
                  setSelectedPlan={setSelectedPlan}
                  countryCode={countryCode}
                  getTitle={getTitle}
                  lessMd={isMobile}
                  onBack={onBack}
                  startingStep={startPaywallStep}
                  paywallLocation={location}
                  isModal={isModal}
                  onCompletePurchase={completePurchase}
                  completePurchaseLoading={completePurchaseLoading}
                  completePurchaseError={completePurchaseError}
                  onPaymentCompleteRedirect={onCompleteRedirect}
                  enableAnnualRenewal={enableAnnualRenewal}
                  isTrialEligible={isTrialEligible}
                  isReactivate={isReactivate}
                  hideIsAnnualToggle={hideIsAnnualToggle}
                />
              )}
            </FlexCol>
            {threeDActionContext?.token && (
              <ThreeDSecureForm
                threeDActionToken={threeDActionContext.token}
                onToken={async (token) => {
                  try {
                    if (threeDActionContext.actionType === ThreeDActionType.ADD_PAYMENT) {
                      const paymentOption = await onAddPaymentOption({ threeDSecureActionResultTokenId: token.id });
                      if (location === PaywallLocation.SIGNUP) {
                        await completePurchase({ billingInfoId: paymentOption.id, termsAccepted: true });
                        showMessage({
                          message: t('common:paywall.payment.success'),
                          color: 'success',
                        });
                        onNext();
                      }
                    }

                    if (threeDActionContext.actionType === ThreeDActionType.PURCHASE) {
                      await completePurchase({ threeDSecureActionResultTokenId: token.id, termsAccepted: true });
                      showMessage({
                        message: t('common:paywall.payment.success'),
                        color: 'success',
                      });
                      onNext();
                    }
                  } catch (error) {
                    // we do not want to display the error notification if the 3DS flow is required
                    if (error?.graphQLErrors?.[0]?.data?.errorData?.threeDSecureActionTokenId) return;

                    showMessage({
                      color: 'paywallError',
                      message: t('common:paywall.payment.error'),
                    });
                    captureExceptionEvent({ location: 'Talent -> 3DS Complete Purchase', error });
                    onThreeDActionError();
                  }
                }}
                onError={onThreeDActionError}
                loading={addPaymentLoading}
              />
            )}
          </RecurlyProvider>
        </Loader>
      </PaywallWrapperWithModal>
    );
  }
);
